import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Plus, Rocket, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";


import { CreateRolloutDialog } from "./CreateRolloutDialog";
import { RolloutDetails } from "./RolloutDetails";


export const PromptRollouts = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedRollout, setSelectedRollout] = useState<string | null>(null);

  const { data: rollouts, isLoading, refetch } = useQuery({
    queryKey: ["prompt-rollouts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_rollouts" as any)
        .select(`
          *,
          prompt_versions:version_id (
            version,
            template_id,
            prompt_templates:template_id (name)
          )
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching rollouts:", error);
        throw error;
      }
      return data;
    },
    retry: 1,
  });

  const handleCreateSuccess = () => {
    refetch();
    setShowCreateDialog(false);
    toast.success("Rollout created successfully");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "paused":
        return "secondary";
      case "completed":
        return "outline";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Prompt Rollouts</h2>
            <p className="text-muted-foreground">Loading rollouts...</p>
          </div>
        </div>
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 bg-muted animate-pulse rounded" />
                    <div>
                      <div className="h-5 w-48 bg-muted animate-pulse rounded mb-2" />
                      <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                  <div className="h-6 w-16 bg-muted animate-pulse rounded" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-4 w-full bg-muted animate-pulse rounded" />
                  <div className="h-4 w-full bg-muted animate-pulse rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (selectedRollout) {
    return (
      <RolloutDetails
        rolloutId={selectedRollout}
        onBack={() => {
          setSelectedRollout(null);
          refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Prompt Rollouts</h2>
          <p className="text-muted-foreground">Manage gradual rollouts and A/B tests</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Rollout
        </Button>
      </div>

      <div className="grid gap-4">
        {rollouts?.map((rollout: any) => (
          <Card
            key={rollout.id}
            className="cursor-pointer hover:bg-accent transition-colors"
            onClick={() => setSelectedRollout(rollout.id)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="text-lg">
                      {rollout.name || rollout.prompt_versions?.prompt_templates?.name || "Unnamed Rollout"}
                      {rollout.prompt_versions?.version && ` v${rollout.prompt_versions.version}`}
                    </CardTitle>
                    <CardDescription>
                      Rollout for {rollout.prompt_versions?.prompt_templates?.name || "prompt template"}
                    </CardDescription>
                  </div>
                </div>
                <Badge variant={getStatusColor(rollout.status)}>
                  {rollout.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium ml-2">
                    {new Date(rollout.created_at).toLocaleDateString()}
                  </span>
                </div>
                {rollout.scheduled_start_at && (
                  <div>
                    <span className="text-muted-foreground">Scheduled:</span>
                    <span className="font-medium ml-2">
                      {new Date(rollout.scheduled_start_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {rollout.started_at && (
                  <div>
                    <span className="text-muted-foreground">Started:</span>
                    <span className="font-medium ml-2">
                      {new Date(rollout.started_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {rollout.completed_at && (
                  <div>
                    <span className="text-muted-foreground">Completed:</span>
                    <span className="font-medium ml-2">
                      {new Date(rollout.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {rollout.requires_manual_approval && (
                  <div>
                    <span className="text-muted-foreground">Requires approval</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {rollouts?.length === 0 && (
          <div className="text-center py-12">
            <Rocket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">No rollouts yet</p>
            <p className="text-muted-foreground">Create your first prompt rollout</p>
          </div>
        )}
      </div>

      <CreateRolloutDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
